import { Badge, Flex, Heading, Input, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormField } from '../../../components/FormField'
import { TableSkeletonRows } from '../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatApiDate, formatDateTime, useAppToast, useHasRole } from '../../../helpers'
import { DutyPlannerService } from '../../../services/DutyPlanner/DutyPlannerService'
import { DutyPlannerListType as DutyPlannerListType } from '../../../services/DutyPlanner/types'
import DutyPlannerFormModal from './DutyPlannerFormModal'

const timeToEvent = (date: string) => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const eventDate = new Date(date)
  const diff = eventDate.getTime() - now.getTime()

  if (diff >= 0) {
    const diffInDays = Math.ceil(diff / (1000 * 3600 * 24))

    if (diffInDays == 0) {
      return <Badge colorScheme="magenta">hoje</Badge>
    }

    if (diffInDays == 1) {
      return <Badge colorScheme="yellow">amanhã</Badge>
    }

    return (
      <Badge colorScheme="green">
        em {diffInDays} dia{diffInDays > 1 ? 's' : ''}
      </Badge>
    )
  }

  return ''
}

export default function DutyPlannerList({
  title,
  orderBy,
  show
}: {
  title: string
  orderBy: { key: string; direction: 'asc' | 'desc' }
  show: 'upcoming' | 'past'
}) {
  const service = new DutyPlannerService()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [duties, setDuties] = useState<DutyPlannerListType>({
    data: [],
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 10
  })
  const [editing, setEditing] = useState<number | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useAppToast()
  const [search, setSearch] = useState('')
  const hasRole = useHasRole()
  const permissions = {
    addHoliday: hasRole('add_holiday'),
    editHoliday: hasRole('edit_holiday'),
    deleteHoliday: hasRole('delete_holiday')
  }

  useEffect(() => {
    loadDuties()
  }, [])

  useEffect(() => {
    loadDuties()
  }, [page, pageSize, search, orderBy, show])

  const handleDelete = async () => {
    if (deleting === null) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(deleting)
      toast.success('Evento excluído')
      setIsDeleteOpen(false)
      loadDuties()
    } catch (error) {
      toast.error('Erro ao excluir evento', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const loadDuties = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ search, orderBy, show, pageSize, page })
      .then((data) => {
        setDuties(data)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os plantões', error)
      })
      .finally(() => setLoading(false))
  }

  return (
    <>
      <Heading as="h2" mb="10" textTransform="uppercase">
        {title}
      </Heading>
      <Flex flex="1" justifyContent="flex-end" alignItems="center">
        <FormField label="Pesquisar" htmlFor="search" m={0} mr={2}>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FormField>
      </Flex>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Título</Th>
              <Th>Data</Th>
              <Th>Dia da semana</Th>
              <Th>Possui plantão?</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="plantões" onRetry={loadDuties} />
            ) : duties.data.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="plantão" />
            ) : (
              duties.data.map((duty) => (
                <Tr key={duty.id}>
                  <Td>{duty.id}</Td>
                  <Td>{duty.title}</Td>
                  <Td>
                    {formatApiDate(duty.date)} {timeToEvent(duty.date)}
                  </Td>
                  <Td>
                    {new Date(duty.date.split('T')[0] + 'T00:00:00')
                      .toLocaleDateString('pt-BR', { weekday: 'long' })
                      .replace(/^./, (str) => str.toUpperCase())}
                  </Td>
                  <Td>
                    {duty.has_duty ? (
                      <>
                        <Badge colorScheme="green">Sim</Badge>
                        <Tooltip
                          label={duty.consultants.map((consultant) => (
                            <Text key={consultant.name}>- {consultant.name}</Text>
                          ))}
                        >
                          <Badge colorScheme="green" ml="2">
                            {duty.consultants.length} consultor{duty.consultants.length == 1 ? '' : 'es'}
                          </Badge>
                        </Tooltip>
                      </>
                    ) : (
                      <Badge colorScheme="red">Não</Badge>
                    )}
                  </Td>
                  <Td>{formatDateTime(duty.created_at, { showSeconds: true })}</Td>
                  <Td>
                    {permissions.editHoliday && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setEditing(duty.id)
                          setIsFormOpen(true)
                        }}
                      />
                    )}
                    {permissions.deleteHoliday && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeleting(duty.id)
                          setIsDeleteOpen(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={duties.last_page}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <DutyPlannerFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadDuties()
        }}
        id={editing}
      />
      {deleting && (
        <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          action="delete"
          entity="evento"
          isLoading={isDeleting}
        />
      )}
    </>
  )
}
